import { Button, CircularProgress } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import specialOnboardingIcon from "../../../../assets/images/special-onboarding-icon.svg";
import AddButton from "../../../../components/element/AddButton";
import AddPassengerDialog from "../../../../components/element/AddPassengerDialog";
import AddVehicleDialog from "../../../../components/element/AddVehicleDialog";
import DeleteAccountDialog from "../../../../components/element/DeleteAccountDialog";
import ErrorSnackbarAlert from "../../../../components/element/ErrorSnackbarAlert";
import SnackbarAlert from "../../../../components/element/SnackbarAlert";
import SpecialOnboardingValidationDialog from "../../../../components/element/SpecialOnboardingValidationDialog";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { StorageContext } from "../../../../services/StorageProvider";
import ROUTE_PATHS from "../../../../utils/routePaths";
import { getAirportData, getFlightNumber } from "../../../../utils/utils";
import "./SpecialOnboarding.css";
import PassengerList from "./Views/PassengerList";
import VehicleList from "./Views/VehicleList";

import NavigationBar from "../../../../components/element/NavigationBar";
import {
  ClearanceStatus,
  TRIP_PHASE_ARRIVAL,
  TRIP_PHASE_DEPARTURE,
} from "../../../../utils/constants";

const SpecialOnboarding = () => {
  const { updateCommercialEscort, addCommercialEscort, fetchCommercialEscort } =
    useAirlineAdminApi();
  const navigate = useNavigate();
  const { type } = useParams();
  const [flightPlanDetail, setFlightPlanDetail] = useState(null);
  const isEditable = type === "edit" || type === "add";
  const [showAddPassengerDialog, setShowAddPassengerDialog] = useState(false);
  const [passengersAdded, setPassengersAdded] = useState([]);
  const [showAddVehicleDialog, setShowAddVehicleDialog] = useState(false);
  const [departureVehiclesAdded, setDepartureVehiclesAdded] = useState([]);
  const [arrivalVehiclesAdded, setArrivalVehiclesAdded] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [tripPhase, setTripPhase] = useState("");
  const [passengerWantToDelete, setPassengerWantToDelete] = useState("");
  const [vehicleWantToDelete, setVehicleWantToDelete] = useState("");
  const { airportData } = useContext(StorageContext);
  const [departureAirportData, setDepartureAirportData] = useState(null);
  const [arrivalAirportData, setArrivalAirportData] = useState(null);

  const { data: commercialEscortService } = useQuery({
    queryKey: [
      "get-commercial-flight-plan-escort-service",
      flightPlanDetail?.id,
    ],
    queryFn: () => fetchCommercialEscort(flightPlanDetail?.id),
    enabled: !!flightPlanDetail?.id,
  });

  const addCommercialEscortMutation = useMutation({
    mutationFn: ({ data }) => addCommercialEscort(data),
    onSuccess: (response) => {
      const vehicleAdded = [...departureVehiclesAdded, ...arrivalVehiclesAdded];
      setPassengersAdded(passengersAdded);
      setDepartureVehiclesAdded(
        vehicleAdded.filter((v) => v.tripPhase === TRIP_PHASE_DEPARTURE)
      );
      setArrivalVehiclesAdded(
        vehicleAdded.filter((v) => v.tripPhase === TRIP_PHASE_ARRIVAL)
      );
      handleBackButton();
    },
  });

  const updateEscortMutation = useMutation({
    mutationFn: ({ data }) => updateCommercialEscort(data),
    onSuccess: (response) => {
      const vehicleAdded = [...departureVehiclesAdded, ...arrivalVehiclesAdded];
      setPassengersAdded(passengersAdded);
      setDepartureVehiclesAdded(
        vehicleAdded.filter((v) => v.tripPhase === TRIP_PHASE_DEPARTURE)
      );
      setArrivalVehiclesAdded(
        vehicleAdded.filter((v) => v.tripPhase === TRIP_PHASE_ARRIVAL)
      );
    },
  });

  useEffect(() => {
    const storedData = localStorage.getItem("flightPlanData");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setFlightPlanDetail(parsedData);
      setDepartureAirportData(
        getAirportData(airportData, parsedData?.departureAirportId)
      );
      setArrivalAirportData(
        getAirportData(airportData, parsedData?.arrivalAirportId)
      );
    }

    if (commercialEscortService?.data) {
      if (commercialEscortService?.data?.passengers) {
        setPassengersAdded(commercialEscortService?.data?.passengers);
      }
      if (commercialEscortService?.data?.vehicles) {
        const departureVehicles =
          commercialEscortService?.data?.vehicles.filter(
            (vehicle) => vehicle.tripPhase === TRIP_PHASE_DEPARTURE
          );
        setDepartureVehiclesAdded(departureVehicles);
        const arrivalVehicles = commercialEscortService?.data?.vehicles.filter(
          (vehicle) => vehicle.tripPhase === TRIP_PHASE_ARRIVAL
        );
        setArrivalVehiclesAdded(arrivalVehicles);
      }
    }
  }, [commercialEscortService]);

  const handleBackButton = () => {
    navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER_EDIT);
  };

  const handleDepartureAddVehicle = () => {
    setTripPhase(TRIP_PHASE_DEPARTURE);
    setShowAddVehicleDialog(true);
  };

  const handleArrivalAddVehicle = () => {
    setTripPhase(TRIP_PHASE_ARRIVAL);
    setShowAddVehicleDialog(true);
  };

  const handleDepartureDeleteVehicle = (index) => {
    setTripPhase(TRIP_PHASE_DEPARTURE);
    setVehicleWantToDelete(index);
  };

  const handleArrivalDeleteVehicle = (index) => {
    setTripPhase(TRIP_PHASE_ARRIVAL);
    setVehicleWantToDelete(index);
  };

  const handlePassengerConfirmDelete = () => {
    const updatedPassengers = [...passengersAdded];
    updatedPassengers.splice(passengerWantToDelete, 1);
    setPassengersAdded(updatedPassengers);
    setPassengerWantToDelete("");
  };

  const handleVehicleConfirmDelete = () => {
    if (tripPhase === TRIP_PHASE_DEPARTURE) {
      const updatedVehicles = [...departureVehiclesAdded];
      updatedVehicles.splice(vehicleWantToDelete, 1);
      setDepartureVehiclesAdded(updatedVehicles);
      setVehicleWantToDelete("");
    } else {
      const updatedVehicles = [...arrivalVehiclesAdded];
      updatedVehicles.splice(vehicleWantToDelete, 1);
      setArrivalVehiclesAdded(updatedVehicles);
      setVehicleWantToDelete("");
    }
  };

  const escortArrivalStatus =
    commercialEscortService?.data?.escortService?.arrivalStatus ===
    ClearanceStatus.NOT_REQUIRED
      ? null
      : commercialEscortService?.data?.escortService?.arrivalStatus;
  const escortDepartureStatus =
    commercialEscortService?.data?.escortService?.departureStatus ===
    ClearanceStatus.NOT_REQUIRED
      ? null
      : commercialEscortService?.data?.escortService?.departureStatus;

  const isArrivedApproved =
    escortArrivalStatus === ClearanceStatus.CLEARANCE_GRANTED ||
    escortArrivalStatus === ClearanceStatus.PAID ||
    escortArrivalStatus === ClearanceStatus.CLEARANCE_REJECTED;

  const isDepartureApproved =
    escortDepartureStatus === ClearanceStatus.CLEARANCE_GRANTED ||
    escortDepartureStatus === ClearanceStatus.PAID ||
    escortDepartureStatus === ClearanceStatus.CLEARANCE_REJECTED;
  
  const hasDepartureTerminal = !!flightPlanDetail?.departureAirportTerminalId;
  const hasArrivalTerminal = !!flightPlanDetail?.arrivalAirportTerminalId;
  
  // Check if special onboarding is allowed
  const isDepartureEscortAllowed = departureAirportData?.escortFeeEnabled && !hasDepartureTerminal;
  const isArrivalEscortAllowed = arrivalAirportData?.escortFeeEnabled && !hasArrivalTerminal;
  const isAnyEscortAllowed = isDepartureEscortAllowed || isArrivalEscortAllowed;

  const handleSaveSpecialOnboarding = () => {
 
    const currentTime = new Date();
    const departureTime = new Date(flightPlanDetail?.departureTime);
    const timeDifference = departureTime - currentTime;

    if (timeDifference < 36 * 60 * 60 * 1000) {
      setShowValidationDialog(true);
    } else {
      // Only include vehicles and passengers from allowed sections
      const vehicleAdded = [
        ...(isDepartureEscortAllowed ? departureVehiclesAdded : []),
        ...(isArrivalEscortAllowed ? arrivalVehiclesAdded : [])
      ];
      
      const passengersToAdd = isDepartureEscortAllowed ? passengersAdded : [];

      if (type === "edit") {
        updateEscortMutation.mutate({
          data: {
            cfpId: flightPlanDetail?.id,
            vehicles: vehicleAdded,
            passengers: passengersToAdd,
          },
        });
      } else {
        addCommercialEscortMutation.mutate({
          data: {
            cfpId: flightPlanDetail?.id,
            passengers: passengersToAdd,
            vehicles: vehicleAdded,
          },
        });
      }
    }
  };

  return (
    <div>
      {updateEscortMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {updateEscortMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}

      {addCommercialEscortMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {addCommercialEscortMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {updateEscortMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          {updateEscortMutation.data?.data}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {addCommercialEscortMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          {addCommercialEscortMutation.data?.data}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <NavigationBar />
      <div className="special-onboard-container">
        <div className="commercial-airline-navigation-bar">
          <div className="commercial-airline-navigation-bar-left">
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER}
                className="navigation-header-link"
              >
                Flight Roster/
              </Link>
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER_EDIT}
                className="navigation-header-link"
              >
                Edit/
              </Link>
              Special Onboarding
            </div>
          </div>
          <div className="commercial-airline-navigation-bar-center">
            <img
              src={specialOnboardingIcon}
              alt="special-onboarding-logo"
              style={{ width: "61px", height: "30px" }}
            />
            <p>Special Onboarding</p>
          </div>
          <div className="commercial-airline-navigation-bar-right">
            {isEditable && (
              <>
                <Button
                  variant="contained"
                  className="special-onboarding-cancel"
                  onClick={handleBackButton}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  className="special-onboarding-save"
                  onClick={handleSaveSpecialOnboarding}
                >
                  {updateEscortMutation?.isPending ||
                  addCommercialEscortMutation?.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="container">
          {departureAirportData?.escortFeeEnabled && !hasDepartureTerminal && (
            <>
              <div className="label">{`${departureAirportData?.name} (Departure)`}</div>
              <div className="top-form-group">
                <label className="label margin-bottom">Flight Number</label>
                <input
                  className="flight-input"
                  type="text"
                  value={getFlightNumber(flightPlanDetail)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label className="label">
                  {!isDepartureApproved && isEditable
                    ? "Add Passengers"
                    : "Passengers"}
                </label>
                {!isDepartureApproved && isEditable ? (
                  <AddButton onClick={() => setShowAddPassengerDialog(true)} />
                ) : null}
              </div>
              {passengersAdded?.length > 0 ? (
                <PassengerList
                  items={passengersAdded}
                  isEditable={isEditable}
                  onDelete={(id) => setPassengerWantToDelete(id)}
                  isApproved={isDepartureApproved}
                />
              ) : null}
              <div className="form-group">
                <label className="label">
                  {!isDepartureApproved && isEditable
                    ? "Add Vehicles"
                    : "Vehicles"}
                </label>
                {!isDepartureApproved && isEditable ? (
                  <AddButton onClick={handleDepartureAddVehicle} />
                ) : null}
              </div>
              {departureVehiclesAdded?.length > 0 && (
                <VehicleList
                  items={departureVehiclesAdded}
                  isEditable={isEditable}
                  onDelete={handleDepartureDeleteVehicle}
                  isApproved={isDepartureApproved}
                />
              )}
              <div className="main-divider" />
            </>
          )}

          {arrivalAirportData?.escortFeeEnabled && !hasArrivalTerminal && (
            <>
              <div className="form-group">
                <label className="label">{`${arrivalAirportData?.name} (Arrival)`}</label>
              </div>
              <div className="form-group">
                <label className="label">
                  {!isArrivedApproved && isEditable
                    ? "Add Vehicles"
                    : "Vehicles"}
                </label>
                {!isArrivedApproved && isEditable ? (
                  <AddButton onClick={handleArrivalAddVehicle} />
                ) : null}
              </div>
              {arrivalVehiclesAdded?.length > 0 ? (
                <VehicleList
                  items={arrivalVehiclesAdded}
                  isEditable={isEditable}
                  onDelete={handleArrivalDeleteVehicle}
                  isApproved={isArrivedApproved}
                />
              ) : null}
            </>
          )}
        </div>
        <AddPassengerDialog
          open={showAddPassengerDialog}
          onClose={() => setShowAddPassengerDialog(false)}
          passengersAdded={passengersAdded}
          setPassengersAdded={setPassengersAdded}
        />
        <AddVehicleDialog
          open={showAddVehicleDialog}
          onClose={() => setShowAddVehicleDialog(false)}
          tripPhase={tripPhase}
          vehicleAdded={
            tripPhase === TRIP_PHASE_DEPARTURE
              ? departureVehiclesAdded
              : arrivalVehiclesAdded
          }
          setVehicleAdded={
            tripPhase === TRIP_PHASE_DEPARTURE
              ? setDepartureVehiclesAdded
              : setArrivalVehiclesAdded
          }
        />
        <DeleteAccountDialog
          open={passengerWantToDelete !== ""}
          onClose={() => setPassengerWantToDelete("")}
          subtitle={"Passenger"}
          onClickDelete={handlePassengerConfirmDelete}
          isLoading={updateEscortMutation?.isPending}
        />
        <DeleteAccountDialog
          open={vehicleWantToDelete !== ""}
          onClose={() => setVehicleWantToDelete("")}
          subtitle={"Vehicle"}
          onClickDelete={handleVehicleConfirmDelete}
          isLoading={updateEscortMutation?.isPending}
        />
        <SpecialOnboardingValidationDialog
          open={showValidationDialog}
          onClose={() => setShowValidationDialog(false)}
        />
        <ErrorSnackbarAlert
          open={validationError !== ""}
          onClose={() => setValidationError("")}
        >
          {validationError}
        </ErrorSnackbarAlert>
      </div>
    </div>
  );
};

export default SpecialOnboarding;
